import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { CircularProgress } from '@mui/material';
import format from 'string-template';

import { PrimaryButton } from '../../../../../../../components/Button/ActionButtons';
import InputLabel from '../../../../../../../components/v2/InputLabel';
import TextField from '../../../../../../../components/v2/TextField';
import useComponentMounted from '../../../../../../../hooks/useComponentMounted';
import {
  AILoadingContainer,
  AIInputContainer,
  AIHelperText,
  AIReviewPanel,
  AIReviewHeader,
  AIReviewChipRow,
  AIReviewChip,
  AIReviewActions,
} from './styles';
import texts from './texts.json';

const AI_STATES = {
  INPUT: 'input',
  LOADING: 'loading',
  REVIEW: 'review',
};

const AIIngredientSearchTab = ({ value, onChange, onAISearch }) => {
  const isComponentMountedRef = useComponentMounted();
  const [aiState, setAiState] = useState(AI_STATES.INPUT);
  const [aiQuery, setAiQuery] = useState('');
  const [proposals, setProposals] = useState([]);
  const [selectedProposals, setSelectedProposals] = useState(new Set());
  const [error, setError] = useState(null);

  const handleSearch = useCallback(async () => {
    const trimmed = aiQuery.trim();
    if (!trimmed) return;
    setAiState(AI_STATES.LOADING);
    setError(null);
    try {
      const result = await onAISearch(trimmed);
      if (!isComponentMountedRef.current) return;
      const ingredients = result?.ingredients ?? [];
      setProposals(ingredients);
      setSelectedProposals(new Set(ingredients.map((i) => i.id)));
      setAiState(AI_STATES.REVIEW);
    } catch {
      if (!isComponentMountedRef.current) return;
      setError(texts.aiError);
      setAiState(AI_STATES.INPUT);
    }
  }, [aiQuery, onAISearch, isComponentMountedRef]);

  const toggleProposal = useCallback((id) => {
    setSelectedProposals((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const resetState = useCallback(() => {
    setAiState(AI_STATES.INPUT);
    setProposals([]);
    setSelectedProposals(new Set());
    setAiQuery('');
    setError(null);
  }, []);

  const handleAdd = useCallback(() => {
    const existingIds = new Set(value.map((e) => e.id));
    const toAdd = proposals.filter((p) => selectedProposals.has(p.id) && !existingIds.has(p.id));
    onChange([...value, ...toAdd]);
    resetState();
  }, [proposals, selectedProposals, onChange, value, resetState]);

  const handleCancel = useCallback(() => {
    resetState();
  }, [resetState]);

  if (aiState === AI_STATES.LOADING) {
    return (
      <AILoadingContainer>
        <CircularProgress size={24} />
      </AILoadingContainer>
    );
  }

  if (aiState === AI_STATES.REVIEW) {
    const selectedCount = selectedProposals.size;
    return (
      <AIReviewPanel>
        <AIReviewHeader>
          {format(texts.aiReviewHeader, { count: proposals.length, query: aiQuery })}
        </AIReviewHeader>
        <AIReviewChipRow>
          {proposals.map((p) => (
            <AIReviewChip
              key={p.id}
              $selected={selectedProposals.has(p.id)}
              onClick={() => toggleProposal(p.id)}
            >
              {p.name}
            </AIReviewChip>
          ))}
        </AIReviewChipRow>
        <AIReviewActions>
          <PrimaryButton
            size="medium"
            disabled={selectedCount === 0}
            onClick={handleAdd}
            $fitToContent
          >
            {format(texts.aiAddButton, { count: selectedCount })}
          </PrimaryButton>
          <PrimaryButton
            size="medium"
            variant="light"
            onClick={handleCancel}
            $fitToContent
          >
            {texts.aiCancelButton}
          </PrimaryButton>
        </AIReviewActions>
      </AIReviewPanel>
    );
  }

  return (
    <AIInputContainer>
      <InputLabel>{texts.aiInputLabel}</InputLabel>
      <TextField
        $withoutMargin
        variant="outlined"
        fullWidth
        multiline
        rows={2}
        value={aiQuery}
        onChange={(e) => setAiQuery(e.target.value)}
        placeholder={texts.aiInputPlaceholder}
        inputProps={{ maxLength: 500 }}
      />
      <AIHelperText>{texts.aiInputHelper}</AIHelperText>
      {error && <AIHelperText $isError>{error}</AIHelperText>}
      <PrimaryButton
        size="medium"
        disabled={!aiQuery.trim()}
        onClick={handleSearch}
        $fitToContent
      >
        {texts.aiSearchButton}
      </PrimaryButton>
    </AIInputContainer>
  );
};

AIIngredientSearchTab.propTypes = {
  value: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  })).isRequired,
  onChange: PropTypes.func.isRequired,
  onAISearch: PropTypes.func.isRequired,
};

export default AIIngredientSearchTab;
